import React, { useState, useEffect } from 'react';
import { useKanban } from './KanbanContext';
import { fetchWorkspacesFromLambda, editUserPermissions } from '../utils/lambdaApi';
import { BOARD_PERMISSION_KEYS, BOARD_PERMISSION_LABELS, getDefaultBoardPermissions } from '../utils/permissionKeys.js';
import './PermissionsManager.css';
import { API_BASE_URL, DEFAULT_HEADERS } from '../utils/config';

function PermissionsManager({ onClose }) {
  const [userEmail, setUserEmail] = useState('');
  const [isAdmin, setIsAdmin] = useState(false);
  const [sendTasks, setSendTasks] = useState(false);
  /** Permisos por tablero: { [boardCode]: { moveBetweenStates: bool, ... } } */
  const [perBoardPermissions, setPerBoardPermissions] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [availableWorkspaces, setAvailableWorkspaces] = useState([]);
  const [selectedWorkspaces, setSelectedWorkspaces] = useState([]);
  const [workspacesLoading, setWorkspacesLoading] = useState(true);
  const [fetchingUserData, setFetchingUserData] = useState(false);
  const [originalCodes, setOriginalCodes] = useState([]);
  
  // Nuevo estado para el buscador de tableros
  const [workspaceSearchTerm, setWorkspaceSearchTerm] = useState('');
  const [filteredWorkspaces, setFilteredWorkspaces] = useState([]);
  
  const { getUserPermissions } = useKanban();
  const userPermissions = getUserPermissions();

  // Filtrar los tableros según el término de búsqueda
  useEffect(() => {
    if (workspaceSearchTerm.trim() === '') {
      setFilteredWorkspaces(availableWorkspaces);
    } else {
      const filtered = availableWorkspaces.filter(workspace =>
        workspace.name.toLowerCase().includes(workspaceSearchTerm.toLowerCase())
      );
      setFilteredWorkspaces(filtered);
    }
  }, [workspaceSearchTerm, availableWorkspaces]);

  // Load all workspaces on component mount
  useEffect(() => {
    const loadAllWorkspaces = async () => {
      try {
        setWorkspacesLoading(true);
        // Use empty string to get all workspaces in admin mode
        const workspaces = await fetchWorkspacesFromLambda('', true, true);
        
        if (workspaces && workspaces.length > 0) {
          // Format workspaces for display and selection
          const formattedWorkspaces = workspaces.map(ws => ({
            uuid: ws.uuid,
            name: ws.workspace,
            code: ws.code
          }));
          
          setAvailableWorkspaces(formattedWorkspaces);
          setFilteredWorkspaces(formattedWorkspaces);
        } else {
          setError('No se pudieron cargar los tableros disponibles');
        }
      } catch (error) {
        console.error('Error al cargar los tableros:', error);
        setError('Error al cargar los tableros: ' + error.message);
      } finally {
        setWorkspacesLoading(false);
      }
    };

    // Only load all workspaces if the user is an admin
    if (userPermissions.admin) {
      loadAllWorkspaces();
    } else {
      setError('Se requieren permisos de administrador para gestionar permisos');
      setWorkspacesLoading(false);
    }
  }, [userPermissions.admin]);

  // Fetch user permissions when email changes
  const loadUserPermissions = async (email) => {
    if (!email.trim()) return;
    
    try {
      setFetchingUserData(true);
      setError('');
      setSuccess('');
      
      // Solo usar permisos/read
      const apiEndpoint = `${API_BASE_URL}/permisos/read`;
      
      // Format the request body according to the API requirements
      const requestBody = { user: email.trim() };
      
      // Log detallado de la petición
      
      
      const response = await fetch(apiEndpoint, {
        method: 'POST',
        headers: DEFAULT_HEADERS,
        body: JSON.stringify(requestBody),
      });
      
      // Log del status de la respuesta
  
      
      const data = await response.json();
      
      // Log de la respuesta
      
      if (!response.ok) {
        throw new Error(data.message || 'Error al obtener permisos del usuario');
      }
      
      let permissions = { admin: false, additionalCodes: [], sendTasks: false, perBoardPermissions: {} };
      
      try {
        let parsedBody = null;
        if (data.body) {
          parsedBody = typeof data.body === 'string' ? JSON.parse(data.body) : data.body;
        } else {
          parsedBody = data;
        }
        
        if (parsedBody.admin !== undefined) permissions.admin = !!parsedBody.admin;
        if (parsedBody.sendTasks !== undefined) permissions.sendTasks = !!parsedBody.sendTasks;
        if (Array.isArray(parsedBody.additionalCodes)) permissions.additionalCodes = parsedBody.additionalCodes;
        else if (Array.isArray(parsedBody['additional-codes'])) permissions.additionalCodes = parsedBody['additional-codes'];

        const defaultBoardPerms = getDefaultBoardPermissions();
        const merge = (target, src) => {
          if (!src || typeof src !== 'object') return target;
          const r = { ...target };
          BOARD_PERMISSION_KEYS.forEach(k => { if (src[k] !== undefined) r[k] = !!src[k]; });
          return r;
        };
        if (parsedBody.perBoardPermissions != null) {
          const raw = typeof parsedBody.perBoardPermissions === 'string'
            ? (() => { try { return JSON.parse(parsedBody.perBoardPermissions); } catch { return {}; } })()
            : parsedBody.perBoardPermissions;
          if (raw && typeof raw === 'object') {
            Object.keys(raw).forEach(code => {
              const p = raw[code];
              if (p && typeof p === 'object') permissions.perBoardPermissions[code] = { ...defaultBoardPerms, ...merge(defaultBoardPerms, p) };
            });
          }
        }
        if (Object.keys(permissions.perBoardPermissions).length === 0) {
          const flat = {};
          BOARD_PERMISSION_KEYS.forEach(k => { if (parsedBody[k] !== undefined) flat[k] = !!parsedBody[k]; });
          const hasFlat = BOARD_PERMISSION_KEYS.some(k => parsedBody[k] !== undefined);
          if (hasFlat && permissions.additionalCodes.length > 0) {
            permissions.additionalCodes.forEach(code => {
              permissions.perBoardPermissions[code] = { ...defaultBoardPerms, ...flat };
            });
          }
        }
      } catch (e) {
        console.error('Error parsing permissions response:', e, data);
      }
      
      setIsAdmin(permissions.admin || false);
      setSendTasks(permissions.sendTasks || false);
      setPerBoardPermissions(permissions.perBoardPermissions || {});

      if (permissions.additionalCodes && permissions.additionalCodes.length > 0) {
        // Match available workspaces with user's workspace codes
        const userWorkspaces = availableWorkspaces.filter(ws => 
          permissions.additionalCodes.includes(ws.code)
        );
        
  
        setSelectedWorkspaces(userWorkspaces);
        
        // Store original codes for comparison when updating
        setOriginalCodes(permissions.additionalCodes);
      } else {
        setSelectedWorkspaces([]);
        setOriginalCodes([]);
      }
      
    } catch (error) {
      console.error('Error al obtener los permisos del usuario:', error);
      // Reset selections on error
      setSelectedWorkspaces([]);
      setOriginalCodes([]);
      setError(`No se pudieron obtener los permisos para ${email}. Puede que sea un usuario nuevo.`);
    } finally {
      setFetchingUserData(false);
    }
  };

  // Handle email input and fetch permissions
  const handleEmailChange = (e) => {
    const email = e.target.value;
    setUserEmail(email);
    
    // Clear permissions when email changes
    if (!email.trim()) {
      setSelectedWorkspaces([]);
      setIsAdmin(false);
      setSendTasks(false);
      setPerBoardPermissions({});
      return;
    }
    
    // Debounce the API call to avoid too many requests
    if (email.includes('@')) {
      // Clear any existing timeout
      if (window.emailTimeout) {
        clearTimeout(window.emailTimeout);
      }
      
      // Indicar que estamos cargando datos antes de hacer la petición
      setFetchingUserData(true);
      
      // Set a new timeout to fetch permissions after typing stops
      window.emailTimeout = setTimeout(() => {
        loadUserPermissions(email);
      }, 500); // Wait 500ms after typing stops
    }
  };

  // Handle workspace selection
  const handleWorkspaceSelection = (workspace) => {
    setSelectedWorkspaces(prevSelected => {
      const isSelected = prevSelected.some(ws => ws.code === workspace.code);
      if (isSelected) {
        setPerBoardPermissions(prev => {
          const next = { ...prev };
          delete next[workspace.code];
          return next;
        });
        return prevSelected.filter(ws => ws.code !== workspace.code);
      }
      setPerBoardPermissions(prev => ({
        ...prev,
        [workspace.code]: prev[workspace.code] || { ...getDefaultBoardPermissions() }
      }));
      return [...prevSelected, workspace];
    });
  };

  const setPermissionForBoard = (boardCode, permissionKey, value) => {
    setPerBoardPermissions(prev => ({
      ...prev,
      [boardCode]: {
        ...(prev[boardCode] || getDefaultBoardPermissions()),
        [permissionKey]: !!value
      }
    }));
  };

  // Limpiar el término de búsqueda
  const clearWorkspaceSearch = () => {
    setWorkspaceSearchTerm('');
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate form
    if (!userEmail.trim()) {
      setError('Por favor, ingrese un correo electrónico válido');
      return;
    }
    
    // Prepare additionalCodes array from selected workspaces
    const additionalCodes = selectedWorkspaces.map(ws => ws.code);
    
    
    
    setIsLoading(true);
    setError('');
    setSuccess('');
    
    try {
      const updates = {
        additionalCodes,
        admin: isAdmin,
        sendTasks,
        perBoardPermissions: selectedWorkspaces.reduce((acc, ws) => {
          acc[ws.code] = perBoardPermissions[ws.code] || getDefaultBoardPermissions();
          return acc;
        }, {})
      };
      
      // Usar editUserPermissions que detecta automáticamente si crear, actualizar o eliminar
      // Pasamos originalCodes para comparar y determinar si necesitamos llamar al endpoint de delete
      const data = await editUserPermissions(userEmail.trim(), updates, originalCodes);
      
      // Generar mensaje de éxito
      let successMessage = '';
      
      if (data.operationType === 'create') {
        successMessage = 'Permisos creados correctamente';
      } else if (data.operationType === 'update') {
        if (data.deletedCodes && data.deletedCodes.length > 0) {
          successMessage = `Permisos actualizados y ${data.deletedCodes.length} código(s) eliminado(s) correctamente`;
        } else {
          successMessage = 'Permisos actualizados correctamente';
        }
      }
      
      // Reset form on success
      setSuccess(successMessage);
      setUserEmail('');
      setIsAdmin(false);
      setSendTasks(false);
      setPerBoardPermissions({});
      setSelectedWorkspaces([]);
      setOriginalCodes([]);
      setWorkspaceSearchTerm('');
      
    } catch (error) {
      console.error('Error al gestionar los permisos:', error);
      setError('Error al gestionar los permisos: ' + error.message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="permissions-manager">
      <div className="permissions-manager-header">
        <h2>Administrador de Permisos</h2>
        <button className="close-btn" onClick={onClose}>✕</button>
      </div>

      {!userPermissions.admin ? (
        <div className="error-container permissions-manager-content">
          <div className="error-message">
            Se requieren permisos de administrador para gestionar permisos de usuarios.
          </div>
        </div>
      ) : (
        <div className="permissions-manager-content">
          {error && (
            <div className="error-message">
              {error}
            </div>
          )}
          
          {success && (
            <div className="success-message">
              {success}
            </div>
          )}
          
                    
          <form onSubmit={handleSubmit} className="permissions-form">
            <div className="form-group">
              <label htmlFor="userEmail">Correo electrónico:</label>
              <div className="email-input-container">
                <input
                  type="email"
                  id="userEmail"
                  placeholder="ejemplo@dominio.com"
                  value={userEmail}
                  onChange={handleEmailChange}
                  required
                />
                {fetchingUserData && (
                  <div className="mini-spinner"></div>
                )}
              </div>
              <small className="form-hint">
                Al ingresar un correo existente se cargarán sus permisos actuales
              </small>
            </div>
            
            <div className="form-group">
              <div className="checkbox-container">
                <input
                  type="checkbox"
                  id="isAdmin"
                  checked={isAdmin}
                  onChange={(e) => setIsAdmin(e.target.checked)}
                />
                <label htmlFor="isAdmin">Permisos de Administrador</label>
              </div>
              <small className="form-hint">
                Los administradores tienen acceso a todos los tableros y pueden gestionar permisos.
              </small>
            </div>
            
            <div className="form-group">
              <div className="checkbox-container">
                <input
                  type="checkbox"
                  id="sendTasks"
                  checked={sendTasks}
                  onChange={(e) => setSendTasks(e.target.checked)}
                />
                <label htmlFor="sendTasks">Puede enviar tareas entre tableros</label>
              </div>
              <small className="form-hint">
                Permite al usuario enviar tareas a otros tableros y mover tareas entre tableros.
              </small>
            </div>

            {/* Sección de tableros adicionales */}
            <div className="form-group">
              <label>Tableros adicionales:</label>
              
              {fetchingUserData ? (
                <div className="loading-container small">
                  <div className="loading-spinner small"></div>
                  <p>Cargando permisos del usuario...</p>
                </div>
              ) : isAdmin ? (
                <div className="admin-highlight permisos-admin-mensaje">
                  El usuario posee permisos de administrador
                </div>
              ) : userEmail ? (
                // Mostrar tableros si hay un correo y no es admin
                <>
                  {workspacesLoading ? (
                    <div className="loading-container small">
                      <div className="loading-spinner small"></div>
                      <p>Cargando tableros...</p>
                    </div>
                  ) : (
                    <>
                      {/* Buscador de tableros */}
                      <div className="workspace-search-container">
                        <input
                          type="text"
                          placeholder="Buscar tablero..."
                          value={workspaceSearchTerm}
                          onChange={(e) => setWorkspaceSearchTerm(e.target.value)}
                          className="workspace-search-input"
                          disabled={fetchingUserData}
                        />
                        {workspaceSearchTerm && (
                          <button 
                            className="clear-search-btn"
                            onClick={clearWorkspaceSearch}
                            title="Limpiar búsqueda"
                          >
                            ✕
                          </button>
                        )}
                      </div>

                      <div className="workspaces-list">
                        {filteredWorkspaces.length > 0 ? (
                          filteredWorkspaces.map((workspace, index) => (
                            <div 
                              key={`${workspace.code}-${index}`}
                              className={`workspace-item-selectable ${
                                selectedWorkspaces.some(ws => ws.code === workspace.code) ? 'selected' : ''
                              }`}
                              onClick={() => handleWorkspaceSelection(workspace)}
                            >
                              <div className="workspace-name">{workspace.name}</div>
                            </div>
                          ))
                        ) : (
                          workspaceSearchTerm ? (
                            <div className="no-results-message">
                              No se encontraron tableros con el término "{workspaceSearchTerm}"
                            </div>
                          ) : (
                            <p className="no-workspaces-message">No hay tableros disponibles</p>
                          )
                        )}
                      </div>
                      <small className="form-hint">
                        {selectedWorkspaces.length === 0 
                          ? 'No hay tableros seleccionados' 
                          : `${selectedWorkspaces.length} tablero(s) seleccionado(s)`}
                      </small>

                      {/* Permisos por cada tablero seleccionado */}
                      {selectedWorkspaces.length > 0 && (
                        <div className="board-permissions-section">
                          <label className="section-label">Permisos por tablero</label>
                          <small className="form-hint" style={{ display: 'block', marginBottom: '10px' }}>
                            Defina qué puede hacer este usuario en cada tablero (puede ser distinto en cada uno).
                          </small>
                          {selectedWorkspaces.map((ws) => {
                            const perms = perBoardPermissions[ws.code] || getDefaultBoardPermissions();
                            return (
                              <div key={ws.code} className="per-board-block">
                                <div className="per-board-title">Permisos para: {ws.name}</div>
                                <div className="board-permissions-grid">
                                  {BOARD_PERMISSION_KEYS.map(key => (
                                    <div key={key} className="checkbox-container board-permission-item">
                                      <input
                                        type="checkbox"
                                        id={`perm-${ws.code}-${key}`}
                                        checked={!!perms[key]}
                                        onChange={(e) => setPermissionForBoard(ws.code, key, e.target.checked)}
                                      />
                                      <label htmlFor={`perm-${ws.code}-${key}`}>{BOARD_PERMISSION_LABELS[key]}</label>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </>
                  )}
                </>
              ) : (
                <p className="placeholder-msg">Ingrese un correo electrónico para administrar permisos de tableros</p>
              )}
            </div>
            
            <div className="form-actions">
              <button
                type="button"
                className="cancel-btn"
                onClick={onClose}
                disabled={isLoading}
              >
                Cancelar
              </button>
              <button
                type="submit"
                className="save-btn"
                disabled={isLoading}
              >
                {isLoading ? (
                  <span>
                    <span className="loading-spinner small"></span>
                    Guardando...
                  </span>
                ) : (
                  'Guardar Permisos'
                )}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

export default PermissionsManager; 